/**
 * Google Drive Individual File Export Service
 * Feature: F068 Enhanced Export
 *
 * Uploads individual markdown files to Google Drive instead of ZIP archives.
 * Maintains folder structure, tracks file metadata, and handles incremental updates.
 */

import { dataRepository } from '../repository/DataRepository';
import { getUserId, restInsert, restSelect } from '../lib/supabaseRest';
import { googleDriveService } from './googleDriveService';
import type { PKMObject } from '../models/types';
import type { BackupInfo } from './exportEnhancedService';
import type {
  DriveFileExportOptions,
  DriveFileExportProgress,
  FolderMapping,
  DriveMarkdownFile,
  BatchOperationResult
} from '../types/drive-export';

/**
 * Upload individual markdown files to Google Drive
 * Maintains folder structure and only uploads changed files
 */
export async function uploadMarkdownFilesToDrive(
  options: DriveFileExportOptions,
  onProgress?: (progress: DriveFileExportProgress) => void
): Promise<BackupInfo | null> {
  const startTime = Date.now();
  let uploaded = 0;
  const errors: Array<{ fileName: string; error: string }> = [];

  try {
    // Check Drive connection
    onProgress?.({
      status: 'starting',
      current: 0,
      total: 0,
      message: 'Checking Google Drive connection...'
    });

    const status = await googleDriveService.getConnectionStatus();
    if (!status.connected) {
      throw new Error('Not connected to Google Drive');
    }

    // Get all non-deleted objects
    onProgress?.({
      status: 'starting',
      current: 0,
      total: 0,
      message: 'Loading objects...'
    });

    const allObjects = await dataRepository.getAllObjects();
    const objects = allObjects.filter(obj => !obj.deletedAt);
    const total = objects.length;

    if (total === 0) {
      throw new Error('No objects found to export');
    }

    console.log(`[DriveFileExport] Exporting ${total} objects as individual files`);

    // Get or create backup folder
    onProgress?.({
      status: 'starting',
      current: 0,
      total,
      message: 'Getting backup folder...'
    });

    const rootFolderId = await googleDriveService.getOrCreateBackupFolder();
    if (!rootFolderId) {
      throw new Error('Failed to get or create backup folder');
    }

    // Create folder structure
    onProgress?.({
      status: 'creating_folders',
      current: 0,
      total,
      message: 'Creating folder structure...'
    });

    const folderMap = await createFolderStructure(
      rootFolderId,
      objects,
      options.organization
    );

    console.log(`[DriveFileExport] Created ${Object.keys(folderMap).length} folders`);

    // Upload each object as individual markdown file
    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];
      const fileName = sanitizeFileName(obj.name || 'untitled') + '.md';

      try {
        // Generate markdown content
        const markdown = await generateMarkdownForObject(obj, options);

        if (!markdown || markdown.length === 0) {
          console.warn(`[DriveFileExport] Empty markdown for ${obj.id}, skipping`);
          continue;
        }

        // Determine folder path and ID
        const folderPath = getFolderPath(obj, options.organization);
        const mapping = folderMap[folderPath];

        if (!mapping) {
          console.error(`[DriveFileExport] No folder mapping for path: ${folderPath}`);
          errors.push({
            fileName,
            error: `No folder mapping for path: ${folderPath}`
          });
          continue;
        }

        // Upload to Drive
        onProgress?.({
          status: 'uploading',
          current: i + 1,
          total,
          message: `Uploading ${fileName}...`,
          currentFileName: fileName,
          successCount: uploaded,
          errorCount: errors.length
        });

        const result = await googleDriveService.uploadMarkdownFile({
          name: fileName,
          content: markdown,
          mimeType: 'text/markdown',
          folderId: mapping.folderId,
          objectId: obj.id
        });

        if (result.success && result.fileId) {
          uploaded++;
          console.log(`[DriveFileExport] ✓ Uploaded ${fileName} (${i + 1}/${total})`);
        } else {
          errors.push({
            fileName,
            error: result.error || 'Unknown error'
          });
          console.error(`[DriveFileExport] ✗ Failed to upload ${fileName}:`, result.error);
        }

      } catch (error: any) {
        errors.push({
          fileName,
          error: error.message || 'Unknown error'
        });
        console.error(`[DriveFileExport] ✗ Error uploading ${fileName}:`, error);
        // Continue with other files
      }
    }

    // Handle attachments if needed
    if (options.includeAttachments) {
      onProgress?.({
        status: 'attachments',
        current: total,
        total,
        message: 'Processing attachments...',
        successCount: uploaded,
        errorCount: errors.length
      });

      // TODO: Implement attachment upload
      // This would follow similar pattern but upload to attachments/ subfolder
      console.log('[DriveFileExport] Attachment upload not yet implemented');
    }

    // Create backup record
    const userId = getUserId();
    if (userId) {
      try {
        const { data, error } = await restInsert<BackupInfo>('backup_history', {
          user_id: userId,
          backup_name: `individual-files-${new Date().toISOString().slice(0, 10)}`,
          storage_path: rootFolderId, // Store root folder ID
          file_size: 0, // Not applicable for individual files
          object_count: uploaded,
          block_count: 0, // TODO: Count blocks
          backup_destination: 'drive',
          drive_folder_id: rootFolderId
        }, {
          returning: true
        });

        if (!error && data && data.length > 0) {
          const backupInfo = data[0];

          // Success message
          const successMsg = errors.length > 0
            ? `Uploaded ${uploaded}/${total} files (${errors.length} errors)`
            : `Uploaded ${uploaded} files successfully`;

          onProgress?.({
            status: 'complete',
            current: total,
            total,
            message: successMsg,
            successCount: uploaded,
            errorCount: errors.length,
            errors: errors.map(e => `${e.fileName}: ${e.error}`)
          });

          console.log(`[DriveFileExport] ✅ Export complete: ${uploaded}/${total} files`);

          return backupInfo;
        }
      } catch (error) {
        console.error('[DriveFileExport] Failed to save backup history:', error);
      }
    }

    // Success but no backup record created
    onProgress?.({
      status: 'complete',
      current: total,
      total,
      message: `Uploaded ${uploaded}/${total} files`,
      successCount: uploaded,
      errorCount: errors.length,
      errors: errors.map(e => `${e.fileName}: ${e.error}`)
    });

    return null;

  } catch (error: any) {
    console.error('[DriveFileExport] Export failed:', error);

    onProgress?.({
      status: 'error',
      current: 0,
      total: 0,
      message: `Export failed: ${error.message}`,
      error: error.message,
      successCount: uploaded,
      errorCount: errors.length
    });

    throw error;
  }
}

/**
 * Create folder structure in Drive
 * Returns mapping of folder paths to Drive folder IDs
 */
async function createFolderStructure(
  parentFolderId: string,
  objects: PKMObject[],
  organization: string
): Promise<Record<string, FolderMapping>> {
  const folderMap: Record<string, FolderMapping> = {
    '': { path: '', folderId: parentFolderId, created: false }
  };

  if (organization === 'flat') {
    // All files in root folder
    return folderMap;
  }

  // Get all unique folder paths needed
  const paths = new Set<string>();
  for (const obj of objects) {
    const path = getFolderPath(obj, organization);
    if (path && !paths.has(path)) {
      paths.add(path);
    }
  }

  console.log(`[DriveFileExport] Creating ${paths.size} folders...`);

  // Create folders in Drive
  for (const path of paths) {
    try {
      const result = await googleDriveService.getOrCreateFolder({
        path,
        parentFolderId
      });

      if (result.success && result.folderId) {
        folderMap[path] = {
          path,
          folderId: result.folderId,
          parentFolderId,
          created: true
        };
        console.log(`[DriveFileExport] Created folder: ${path} -> ${result.folderId}`);
      } else {
        console.error(`[DriveFileExport] Failed to create folder ${path}:`, result.error);
      }
    } catch (error) {
      console.error(`[DriveFileExport] Error creating folder ${path}:`, error);
    }
  }

  return folderMap;
}

/**
 * Generate markdown content for a single object
 */
async function generateMarkdownForObject(
  obj: PKMObject,
  options: DriveFileExportOptions
): Promise<string> {
  let markdown = '';

  // Add YAML frontmatter if enabled
  if (options.includeFrontmatter) {
    const frontmatter: Record<string, any> = {};
    const fields = options.frontmatterFields || ['id', 'type', 'status', 'created', 'updated'];

    for (const field of fields) {
      switch (field) {
        case 'id':
          frontmatter.id = obj.id;
          break;
        case 'type':
          frontmatter.type = obj.type;
          break;
        case 'status':
          frontmatter.status = obj.status;
          break;
        case 'tags':
          // Tags might be in properties - TODO: implement when property system ready
          frontmatter.tags = [];
          break;
        case 'created':
          frontmatter.created = obj.createdAt;
          break;
        case 'updated':
          frontmatter.updated = obj.updatedAt;
          break;
      }
    }

    markdown += '---\n';
    for (const [key, value] of Object.entries(frontmatter)) {
      if (Array.isArray(value)) {
        markdown += `${key}: [${value.join(', ')}]\n`;
      } else {
        markdown += `${key}: ${value}\n`;
      }
    }
    markdown += '---\n\n';
  }

  // Add title
  markdown += `# ${obj.name || 'Untitled'}\n\n`;

  // Add description if present
  if (obj.description) {
    markdown += `${obj.description}\n\n`;
  }

  // Add blocks content
  try {
    const blocks = await dataRepository.getBlocksForObject(obj.id);

    if (blocks.length > 0) {
      markdown += '## Content\n\n';

      for (const block of blocks) {
        switch (block.type) {
          case 'text':
            if (block.content) {
              markdown += `${block.content}\n\n`;
            }
            break;
          case 'heading':
            const level = block.metadata?.level || 2;
            markdown += `${'#'.repeat(level)} ${block.content}\n\n`;
            break;
          case 'bullet_list':
          case 'numbered_list':
            if (block.content) {
              const prefix = block.type === 'bullet_list' ? '-' : '1.';
              markdown += `${prefix} ${block.content}\n`;
            }
            break;
          case 'code':
            const lang = block.metadata?.language || '';
            markdown += `\`\`\`${lang}\n${block.content}\n\`\`\`\n\n`;
            break;
          case 'quote':
            markdown += `> ${block.content}\n\n`;
            break;
          case 'image':
            const imagePath = block.metadata?.imagePath || '';
            const altText = block.metadata?.altText || 'image';
            markdown += `![${altText}](${imagePath})\n\n`;
            break;
          default:
            // Generic fallback
            if (block.content) {
              markdown += `${block.content}\n\n`;
            }
        }
      }
    }
  } catch (error) {
    console.error(`[DriveFileExport] Error loading blocks for ${obj.id}:`, error);
  }

  return markdown.trim();
}

/**
 * Get folder path based on organization strategy
 */
function getFolderPath(obj: PKMObject, strategy: string): string {
  switch (strategy) {
    case 'type':
      // Organize by object type: notes/, tasks/, projects/
      return obj.type || 'misc';

    case 'collection':
      // Use first collection or 'uncategorized'
      // TODO: Load collections from database when collection feature implemented
      return 'uncategorized';

    case 'tag':
      // Use first tag or 'untagged'
      // Tags might be stored in properties
      return 'untagged';

    case 'hybrid':
      // type/collection structure
      return `${obj.type || 'misc'}/uncategorized`;

    case 'flat':
    default:
      // All files in root
      return '';
  }
}

/**
 * Sanitize filename for filesystem
 */
function sanitizeFileName(name: string): string {
  return name
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid chars
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_{2,}/g, '_') // Collapse multiple underscores
    .substring(0, 100) // Limit length
    .replace(/^_+|_+$/g, ''); // Trim underscores from ends
}
